// FTEDN Team — Dashboard Page (wired to real analytics + moderation queue)
import { useEffect, useState } from 'react';
import { apiClient } from '../../hooks/useApi';
import { StatsCard } from './StatsCard';
import {
  mapDashboardStats,
  formatStatNumber,
  mapQueueItem,
  sortQueueBySeverity,
  getUrgencyLabel,
  validateQuickAction,
  type DashboardStats,
  type ModerationQueueItem,
} from './dashboard.helpers';
import './DashboardPage.css';

export function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [queue, setQueue] = useState<ModerationQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [reasonDrafts, setReasonDrafts] = useState<Record<string, string>>({});
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [analytics, mod] = await Promise.allSettled([
          apiClient.get('/analytics/overview'),
          apiClient.get('/moderation/queue'),
        ]);

        if (analytics.status === 'fulfilled' && analytics.value.success) {
          setStats(mapDashboardStats(analytics.value.data));
        } else {
          setError('Dashboard analytics are unavailable.');
        }
        if (mod.status === 'fulfilled' && mod.value.ok && Array.isArray(mod.value.items)) {
          setQueue(sortQueueBySeverity(mod.value.items.map(mapQueueItem)));
        } else {
          setError(prev => prev ? `${prev} Moderation queue is unavailable.` : 'Moderation queue is unavailable.');
        }
      } catch {
        setError('Dashboard data is unavailable.');
      }
      setLoading(false);
    }
    load();
  }, []);

  const handleAction = async (action: string, item: ModerationQueueItem) => {
    const reason = (reasonDrafts[item.id] ?? '').trim();
    const validation = validateQuickAction(action, item.id, reason);
    if (!validation.valid) {
      setActionErrors(prev => ({ ...prev, [item.id]: validation.error }));
      return;
    }

    setActionErrors(prev => {
      const next = { ...prev };
      delete next[item.id];
      return next;
    });
    setActionLoading(item.id);
    try {
      await apiClient.post('/moderation/action', {
        action,
        contentRef: item.contentRef,
        targetUserId: item.targetUserId,
        reason,
        reportId: item.source === 'report' ? item.id : undefined,
        flagId: item.source === 'auto-flag' ? item.id : undefined,
      });
      setQueue(prev => prev.filter(q => q.id !== item.id));
      setReasonDrafts(prev => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    } catch {
      setError(`Failed to ${action} moderation item ${item.id}.`);
    }
    setActionLoading(null);
  };

  const s = stats ?? { totalCreators: 0, totalContent: 0, totalCampaigns: 0, pendingModeration: 0, activeTrials: 0, signupsToday: 0 };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Team Dashboard</h1>
        <p className="dashboard-subtitle">Creator management & operations overview</p>
      </div>

      {error && <div className="dashboard-error" role="alert">{error}</div>}

      <div className="stats-grid">
        <StatsCard label="Total Creators" value={loading ? '...' : formatStatNumber(s.totalCreators)} icon="👥" accent="sage" />
        <StatsCard label="Pending Review" value={loading ? '...' : String(s.pendingModeration)} icon="📋" accent="navy" />
        <StatsCard label="Active Campaigns" value={loading ? '...' : String(s.totalCampaigns)} icon="🎯" accent="lime" />
        <StatsCard label="Content Pieces" value={loading ? '...' : formatStatNumber(s.totalContent)} icon="📝" accent="sage" />
        <StatsCard label="Active Trials" value={loading ? '...' : String(s.activeTrials)} icon="🧪" accent="navy" />
        <StatsCard label="Signups Today" value={loading ? '...' : String(s.signupsToday)} icon="📈" accent="lime" />
      </div>

      <div className="dashboard-grid">
        {/* Moderation Queue — Quick Actions */}
        <div className="neo-card moderation-panel">
          <h2 className="card-title">
            ⚡ Quick Actions
            {queue.length > 0 && <span className="queue-badge">{queue.length}</span>}
          </h2>
          {queue.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">✅</span>
              <p>All clear — no pending moderation items</p>
            </div>
          ) : (
            <div className="queue-list">
              {queue.slice(0, 10).map(item => {
                const urgency = getUrgencyLabel(item.score);
                const reasonInputId = `dashboard-quick-action-reason-${item.id}`;
                const actionError = actionErrors[item.id];
                return (
                  <div
                    key={item.id}
                    className={`queue-item urgency-${urgency.level}`}
                    aria-label={`Quick moderation item ${item.id}`}>
                    <div className="queue-item-header">
                      <span className="queue-urgency">{urgency.label}</span>
                      <span className="queue-source">{item.source === 'auto-flag' ? '🤖 Auto' : '🏳️ Report'}</span>
                    </div>
                    <p className="queue-reason">{item.reason}</p>
                    <label className="queue-reason-label" htmlFor={reasonInputId}>
                      Reason
                    </label>
                    <textarea
                      id={reasonInputId}
                      className="queue-reason-input"
                      rows={2}
                      value={reasonDrafts[item.id] ?? ''}
                      aria-label={`Reason for ${item.reason}`}
                      aria-invalid={Boolean(actionError)}
                      aria-describedby={actionError ? `${reasonInputId}-error` : undefined}
                      onChange={event => {
                        const value = event.target.value;
                        setReasonDrafts(prev => ({ ...prev, [item.id]: value }));
                        if (actionError) {
                          setActionErrors(prev => {
                            const next = { ...prev };
                            delete next[item.id];
                            return next;
                          });
                        }
                      }}
                    />
                    {actionError && (
                      <p id={`${reasonInputId}-error`} className="queue-action-error" role="alert">
                        {actionError}
                      </p>
                    )}
                    <div className="queue-actions">
                      <button
                        className="action-btn approve"
                        disabled={actionLoading === item.id}
                        onClick={() => handleAction('hide', item)}>
                        Hide
                      </button>
                      <button
                        className="action-btn reject"
                        disabled={actionLoading === item.id}
                        onClick={() => handleAction('delete', item)}>
                        Delete
                      </button>
                      <button
                        className="action-btn warn"
                        disabled={actionLoading === item.id}
                        onClick={() => handleAction('warn', item)}>
                        Warn
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="neo-card">
          <h2 className="card-title">📊 Recent Activity</h2>
          <div className="empty-state">
            <span className="empty-icon">📊</span>
            <p>Activity will appear here as creators submit content</p>
          </div>
        </div>
      </div>
    </div>
  );
}
